import { ECONOMY_FLOW } from "@/data/content";
import PageHero from "@/components/arc/PageHero";
import Reveal from "@/components/arc/Reveal";
import Marquee from "@/components/arc/Marquee";
import { GlowButton, Panel, SectionHeader, SimTag } from "@/components/arc/ui";
import { GlowOrbs } from "@/components/arc/Backdrops";

const Economy = () => (
  <>
    <PageHero
      index="06"
      eyebrow="ECONOMY"
      title="THE MACHINE ECONOMY"
      sub="The next economy may not be composed only of humans. ARCIMUS explores machines operating within an on-chain economic environment — with USDC as the conceptual payment rail of the Arc ecosystem."
    />

    <section className="relative overflow-hidden py-20 md:py-28">
      <GlowOrbs />
      <div className="relative mx-auto max-w-[1600px] px-5 md:px-8">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {[
            { title: "CONSUME", body: "Machines can consume resources — compute, energy, bandwidth, data." },
            { title: "PROVIDE", body: "Machines can provide services — analysis, coordination, logistics, execution." },
            { title: "COORDINATE", body: "Machines can coordinate — negotiating tasks and sharing objectives with other machines." },
            { title: "TRANSACT", body: "Machines can transact — settling value across the USDC rails of the Arc ecosystem." },
          ].map((card, i) => (
            <Reveal key={card.title} delay={i * 0.08}>
              <Panel className="h-full p-7 transition-shadow duration-300 hover:shadow-[0_0_36px_rgba(22,119,255,0.22)]">
                <span className="font-term text-[10px] tracking-[0.3em] text-electric">
                  0{i + 1}
                </span>
                <h3 className="mt-3 font-display text-lg font-bold tracking-[0.2em] text-chrome">{card.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-silver">{card.body}</p>
              </Panel>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="relative border-y border-ice/10 bg-space/50 py-20 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 md:px-8">
        <Reveal>
          <SectionHeader index="01" eyebrow="THE LOOP" title="HOW VALUE MOVES" align="center" />
        </Reveal>
        <div className="mx-auto mt-14 max-w-3xl" data-testid="economy-flow">
          {ECONOMY_FLOW.map((step, i) => (
            <Reveal key={step.key} delay={i * 0.07}>
              <div className="relative">
                <Panel className="p-6 md:p-7">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="font-display text-base font-bold tracking-[0.22em] text-ice md:text-lg">
                      {step.label}
                    </h3>
                    <span className="font-term text-[10px] tracking-[0.3em] text-silver/60">
                      STAGE {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-silver">{step.body}</p>
                </Panel>
                {i < ECONOMY_FLOW.length - 1 && (
                  <div className="flex justify-center py-2" aria-hidden="true">
                    <span className="h-6 w-px bg-gradient-to-b from-electric to-transparent shadow-[0_0_8px_rgba(0,168,255,0.8)]" />
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="relative py-20 md:py-28">
      <div className="mx-auto grid max-w-[1600px] items-center gap-12 px-5 md:px-8 lg:grid-cols-2">
        <Reveal>
          <div>
            <SectionHeader index="02" eyebrow="SETTLEMENT" title="THE USDC RAIL" />
            <p className="mt-8 text-base leading-relaxed text-silver md:text-lg">
              Inside the ARCIMUS concept, machines price services and settle value in USDC — a
              stable unit of account suited to machine-speed commerce. This is the economic
              environment the Arc ecosystem is designed around.
            </p>
            <div className="mt-8">
              <SimTag>CONCEPT — NO LIVE INTEGRATION</SimTag>
            </div>
            <p className="mt-6 font-term text-[10px] leading-relaxed tracking-[0.2em] text-silver/60">
              ARCIMUS CLAIMS NO PARTNERSHIP, INTEGRATION, OR ENDORSEMENT. USDC AND ARC ARE
              REFERENCED AS THE CONCEPTUAL SETTING ONLY.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <Panel className="scanlines p-8 md:p-10">
            <p className="hud-label">ECONOMIC LAYER {"//"} READOUT</p>
            <div className="mt-6 space-y-4 font-term text-xs leading-7 text-ice/90 md:text-sm">
              <p>{">"} RAIL ......... USDC <span className="text-silver/60">(CONCEPTUAL)</span></p>
              <p>{">"} ECOSYSTEM .... ARC</p>
              <p>{">"} PARTICIPANTS . HUMANS / AGENTS / MACHINES</p>
              <p>{">"} SETTLEMENT ... <span className="text-electric">ON-CHAIN — VISION</span></p>
              <p>{">"} STATUS ....... DESIGN STUDY</p>
            </div>
            <div className="glow-line mt-8" />
            <div className="mt-8">
              <GlowButton to="/roadmap" variant="ghost" testId="economy-roadmap-btn">
                SEE THE PRIME DIRECTIVE
              </GlowButton>
            </div>
          </Panel>
        </Reveal>
      </div>
    </section>

    <Marquee />
  </>
);

export default Economy;
